import { useState } from "react";
import { Settings as SettingsIcon, KeyRound, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Settings() {
    const { user, refreshUser } = useAuth();
    const [apiKey, setApiKey] = useState("");
    const [saving, setSaving] = useState(false);

    const saveKey = async () => {
        setSaving(true);
        try {
            await api.put("/user/settings", { anthropic_api_key: apiKey });
            await refreshUser();
            setApiKey("");
            toast.success(apiKey ? "Your Anthropic key is saved." : "Custom key cleared. Falling back to shared companion.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto w-full px-6 md:px-10 py-10 md:py-14" data-testid="settings-page">
            <div className="mb-10 pl-12 md:pl-0">
                <div className="flex items-center gap-2 text-brand-charcoal/60 text-xs uppercase tracking-widest">
                    <SettingsIcon className="w-3 h-3 text-brand-spark" strokeWidth={2} />
                    Settings
                </div>
                <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-brand-darkPurple leading-tight tracking-tight">
                    Under the hood.
                </h1>
                <p className="mt-3 text-brand-charcoal/75 max-w-2xl">
                    Your companion runs on Claude Sonnet 5. You can plug in your own Anthropic API key for total privacy and control.
                </p>
            </div>

            <div className="rounded-2xl bg-white border border-brand-darkPurple/10 shadow-soft-card p-7 md:p-8 space-y-6">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-creamDeep flex items-center justify-center shrink-0">
                        <KeyRound className="w-4 h-4 text-brand-spark" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-serif text-2xl text-brand-darkPurple">Anthropic API key</h2>
                        <p className="text-sm text-brand-charcoal/70 mt-1">
                            Get one at <a className="underline hover:text-brand-spark" href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">console.anthropic.com</a>. It stays in your account, encrypted in transit.
                        </p>
                        {user?.has_custom_key && (
                            <div className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full" data-testid="key-status-active">
                                <Check className="w-3.5 h-3.5" /> Your personal key is active
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <Label className="text-brand-darkPurple text-sm">Your Anthropic key</Label>
                    <Input
                        data-testid="settings-anthropic-key-input"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        type="password"
                        placeholder={user?.has_custom_key ? "•••• stored securely — enter a new key to replace" : "sk-ant-…"}
                        className="mt-1.5 bg-brand-cream/50 border-brand-darkPurple/10 h-11 focus-visible:ring-brand-gold/50 font-mono text-sm"
                    />
                </div>

                <div className="flex flex-wrap justify-end gap-2 pt-2">
                    {user?.has_custom_key && (
                        <Button
                            data-testid="settings-clear-key-btn"
                            variant="ghost"
                            onClick={async () => { setApiKey(""); await api.put("/user/settings", { anthropic_api_key: "" }); await refreshUser(); toast.success("Custom key cleared."); }}
                            className="text-brand-charcoal hover:bg-brand-darkPurple/5 rounded-full"
                        >
                            Clear my key
                        </Button>
                    )}
                    <Button
                        data-testid="settings-save-key-btn"
                        onClick={saveKey}
                        disabled={saving || !apiKey}
                        className="bg-brand-darkPurple hover:bg-brand-darkPurpleSoft text-white rounded-full px-7"
                    >
                        {saving ? "Saving…" : "Save key"}
                    </Button>
                </div>
            </div>

            <div className="mt-6 rounded-2xl bg-brand-creamDeep/50 border border-brand-darkPurple/8 p-6 text-sm text-brand-charcoal/75 leading-relaxed">
                <div className="font-serif text-brand-darkPurple text-lg mb-1">A quiet note.</div>
                Everything you share with Lantern is yours. Conversations, saved insights, mood logs — all live in a private space tied to your account. If you set your own API key, none of your conversation content flows through our shared companion.
            </div>
        </div>
    );
}
